import React, {useId, useRef, useState} from "react";
import {Input} from "reactstrap";

// Case- and accent-insensitive, so "joao" finds "João".
const normalize = (text) =>
    text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

/**
 * A select you can type into to filter. Shows the chosen option when closed;
 * click or type to open the list. Arrow keys move, Enter picks, Escape closes.
 *
 * @template {string} V
 * @param {{
 *   id?: string,
 *   options: {value: V, label: string, search?: string}[],
 *   value: V|"",
 *   onChange: (value: V) => void,
 *   ariaLabel: string,
 *   placeholder?: string,
 *   emptyMessage?: string,
 *   invalid?: boolean,
 *   autoFocus?: boolean,
 * }} props `search` adds extra text to match against (besides the label).
 */
const SearchSelect = ({
    id,
    options,
    value,
    onChange,
    ariaLabel,
    placeholder,
    emptyMessage = "Nada encontrado.",
    invalid = false,
    autoFocus = false,
}) => {
    const listId = useId();
    const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null));
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    // An Escape that closed the list must not also close a modal around it.
    const escapeUsed = useRef(false);

    const selected = options.find((o) => o.value === value);
    const needle = normalize(query.trim());
    const matches = options.filter((o) => normalize(`${o.label} ${o.search ?? ""}`).includes(needle));

    const close = () => {
        setOpen(false);
        setQuery("");
    };

    const pick = (option) => {
        onChange(option.value);
        close();
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
                setOpen(true);
                return;
            }
            const step = event.key === "ArrowDown" ? 1 : -1;
            setActive((i) => (i + step + matches.length) % Math.max(matches.length, 1));
        } else if (event.key === "Enter") {
            // Never submit the form from here.
            event.preventDefault();
            if (open && matches[active]) pick(matches[active]);
        } else if (event.key === "Escape" && open) {
            event.stopPropagation();
            escapeUsed.current = true;
            close();
        }
    };

    // Modals close on the key's release, after the list is already closed.
    const handleKeyUp = (event) => {
        if (event.key === "Escape" && escapeUsed.current) {
            event.stopPropagation();
            escapeUsed.current = false;
        }
    };

    return (
        <div className="position-relative">
            <Input
                id={id}
                innerRef={inputRef}
                role="combobox"
                aria-label={ariaLabel}
                aria-expanded={open}
                aria-controls={listId}
                aria-autocomplete="list"
                autoComplete="off"
                placeholder={placeholder}
                invalid={invalid}
                autoFocus={autoFocus}
                // While open the box is for typing; closed, it shows the choice.
                value={open ? query : (selected?.label ?? "")}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                    setOpen(true);
                }}
                onBlur={close}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
            />
            {open && (
                <div
                    id={listId}
                    role="listbox"
                    className="list-group position-absolute w-100 shadow-sm mt-1"
                    style={{zIndex: 1000, maxHeight: 240, overflowY: "auto"}}
                >
                    {matches.length === 0 && <div className="list-group-item text-muted">{emptyMessage}</div>}
                    {matches.map((option, index) => (
                        <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={option.value === value}
                            className={
                                "list-group-item list-group-item-action text-start" +
                                (index === active ? " active" : "") +
                                (option.value === value && index !== active ? " fw-semibold" : "")
                            }
                            // Keep focus in the box so the click isn't lost to a blur.
                            onMouseDown={(event) => event.preventDefault()}
                            onMouseEnter={() => setActive(index)}
                            onClick={() => pick(option)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchSelect;
